<template>
    <div class="relative flex items-center justify-center min-h-screen overflow-hidden bg-gray-50">
        <!-- Loading state -->
        <div v-if="isLoading" class="w-full max-w-sm p-6 mx-auto border border-gray-100 rounded-md shadow">
            <div class="flex flex-col space-x-4 animate-pulse">
                <div class="w-24 h-24 mx-auto mb-4 bg-gray-300 rounded-full"></div>
                <div class="items-center flex-1 py-1 mx-auto space-y-4">
                    <div class="w-3/5 h-4 mx-auto bg-gray-300 rounded"></div>
                    <div class="space-y-4">
                        <div class="w-1/3 h-6 mx-auto bg-gray-300 rounded"></div>
                    </div>
                    <div class="w-3/4 h-8 mx-auto bg-gray-300 rounded-md"></div>
                </div>
            </div>
        </div>
        <div v-else-if="notFound" class="px-4 py-5 sm:p-6">
            <div class="max-w-md overflow-hidden bg-white rounded-lg shadow">
                <div class="flex flex-col flex-1 p-8 text-center min-w-sm">
                    <h3 class="text-2xl font-medium leading-5 text-gray-700">Invite Not Found</h3>
                    <h3 class="mt-2 font-medium leading-5 text-gray-400 text-md">This invite could not be found.</h3>
                    <router-link
                        tag="button"
                        to="/dashboard"
                        class="w-full py-3 mt-4 text-white bg-green-400 focus:outline-none focus:shadow-outline hover:bg-green-500 active:bg-green-400 btn"
                    >
                        Continue to Flowist
                    </router-link>
                </div>
            </div>
        </div>
        <!-- Invalid invite -->
        <div v-else-if="isInvalidInvite" class="px-4 py-5 sm:p-6">
            <div class="max-w-md overflow-hidden bg-white rounded-lg shadow">
                <div class="flex flex-col flex-1 p-8 text-center min-w-sm">
                    <h3 class="text-2xl font-medium leading-5 text-gray-700">Invalid Invite</h3>
                    <h3 class="mt-2 font-medium leading-5 text-gray-400 text-md">This invite link is no longer active.</h3>
                    <router-link
                        tag="button"
                        to="/dashboard"
                        class="w-full py-3 mt-4 text-white bg-green-400 focus:outline-none focus:shadow-outline hover:bg-green-500 active:bg-green-400 btn"
                    >
                        Continue to Flowist
                    </router-link>
                </div>
            </div>
        </div>
        <!-- Valid invite but not logged in and may need to register -->
        <div v-else-if="!isInvalidInvite && !isLoggedIn" class="px-4 py-5 sm:p-6">
            <div class="max-w-md overflow-hidden bg-white rounded-lg shadow">
                <div class="flex flex-col flex-1 p-8 text-center min-w-sm">
                    <p v-if="error !== ''" class="py-2 text-sm font-semibold text-red-500">
                        {{ error }}
                    </p>
                    <org-avatar
                        :org="org"
                        class="flex items-center justify-around flex-shrink-0 w-24 h-24 mx-auto text-5xl tracking-tight text-white uppercase rounded-full mx-atuo bg-primary-500 m-2px"
                    />
                    <h3 class="mt-6 text-sm font-medium leading-5 text-gray-400">You were invited to join</h3>
                    <h3 class="mt-2 text-xl font-medium leading-7 text-gray-700">{{ org.name }}</h3>
                    <span class="flex mx-auto mt-3 items-center px-2.5 py-0.5 rounded-lg text-sm font-medium leading-5 bg-gray-100 text-gray-800">
                        <svg class="-ml-0.5 mr-1.5 h-2 w-2 text-primary-500" fill="currentColor" viewBox="0 0 8 8">
                            <circle cx="4" cy="4" r="3" />
                        </svg>
                        {{ org.memberships.length }} members
                    </span>
                    <simple-signup-form @join="join" />
                </div>
            </div>
        </div>
        <!-- Registered and logged in -->
        <div v-else class="px-4 py-5 sm:p-6">
            <div class="max-w-md overflow-hidden bg-white rounded-lg shadow">
                <div class="flex flex-col flex-1 p-8 text-center min-w-sm">
                    <p v-if="error !== ''" class="py-2 text-sm font-semibold text-red-500">
                        {{ error }}
                    </p>
                    <org-avatar
                        :org="org"
                        class="flex items-center justify-around flex-shrink-0 w-32 h-32 mx-auto text-5xl tracking-tight text-white uppercase rounded-full mx-atuo bg-primary-500 m-2px"
                    />
                    <h3 class="mt-6 text-sm font-medium leading-5 text-gray-400">You were invited to join</h3>
                    <h3 class="mt-2 text-xl font-medium leading-7 text-gray-700">{{ org.name }}</h3>
                    <span class="flex mx-auto mt-3 items-center px-2.5 py-0.5 rounded-lg text-sm font-medium leading-5 bg-gray-100 text-gray-800">
                        <svg class="-ml-0.5 mr-1.5 h-2 w-2 text-primary-500" fill="currentColor" viewBox="0 0 8 8">
                            <circle cx="4" cy="4" r="3" />
                        </svg>
                        {{ org.memberships.length }} members
                    </span>
                    <button @click="join" class="w-full py-3 mt-4 text-white bg-green-400 focus:outline-none focus:shadow-outline hover:bg-green-500 active:bg-green-400 btn">
                        Accept Invite
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SimpleSignupForm from '@/components/SimpleSignupForm';
import OrgAvatar from '@/components/OrgAvatar';
import api from '@/client';
import Cookie from 'js-cookie';

export default {
    name: 'app-invite',
    components: { OrgAvatar, SimpleSignupForm },
    data() {
        return {
            isLoading: true,
            notFound: false,
            token: '',
            invite: '',
            org: '',
            error: '',
        };
    },
    mounted() {
        this.isLoading = true;
        this.token = this.$route.params.pathMatch;
        api.get(`invites/${this.token}`)
            .then(resp => {
                this.invite = resp.data.data;
                api.get(`org/${resp.data.data.org_id}`).then(resp => {
                    this.isLoading = false;
                    this.org = resp.data.data;
                });
            })
            .catch(err => {
                if (err.response.status === 404) {
                    this.notFound = true;
                    this.isLoading = false;
                }
            });
    },
    computed: {
        isLoggedIn() {
            return !!Cookie.get('logged_in');
        },
        isInvalidInvite() {
            return this.invite.active === false;
        },
    },
    methods: {
        join() {
            const invite_link = process.env.VUE_APP_BASE_URL + this.$route.path.substring(1);
            this.$store
                .dispatch('orgs/join', invite_link)
                .then(() => this.$router.push('/dashboard'))
                .catch(() => (this.error = 'The invite might be expired or revoked.'));
        },
    },
};
</script>
